import React, { useState } from 'react';

import roundToGivenDecimals from 'common/roundToGivenDecimals';
import getHashrateValueWithUnit from 'common/getHashrateValueWithUnit';
import IHashRate from 'common/types/IHashRate';
import IHashRateUnits from 'common/types/IHashRateUnits';
import { GRAPH_WIDTH, AxisTick, formatBucketTime } from 'common/timeAxis';
import IgraphValuesLinkedList from './IgraphValuesLinkedList';

import './hashrate-graph.scss';
import GraphFrame from './GraphFrame';
import GraphLoading from './GraphLoading';

// The three plottable series, in draw order (areas layer back-to-front).
type SeriesKey = 'hashrate' | 'scoring_hashrate' | 'workers_active';
const SERIES_KEYS: SeriesKey[] = ['hashrate', 'scoring_hashrate', 'workers_active'];

// A point ready to plot: x by real time, one y per series.
interface PlotPoint {
  t: number;
  x: number;
  hashrate: number;
  scoring_hashrate: number;
  workers_active: number;
}

const fmtHashrate = (value: number) => {
  const { unit, coefficient } = getHashrateValueWithUnit(value);

  return `${roundToGivenDecimals(value / coefficient, 2)} ${unit}h/s`;
};

const HashrateGraph = ({
  graphVisibleElements,
  hashrateMaximized,
  hashRatesSlice,
  isLoading,
  maxWorkers,
  max,
  min,
  startMs,
  endMs,
  bucketMs,
  ticks,
  timezone,
}: {
  graphVisibleElements: Array<keyof IgraphValuesLinkedList>
  hashrateMaximized: boolean
  hashRatesSlice: Array<IHashRate>
  hashrateUnits: IHashRateUnits
  isLoading: boolean
  maxWorkers: number
  max: number
  min: number
  startMs: number
  endMs: number
  bucketMs: number
  ticks: AxisTick[]
  timezone?: string
}) => {

  // Index of the bucket currently under the cursor (a single crosshair tooltip
  // that snaps to the nearest point, rather than per-segment hit targets).
  const [hover, setHover] = useState<number | null>(null);

  const span = endMs - startMs || 1;
  // Guard every denominator: a flat series (max === min) or an empty slice
  // (max/maxWorkers === 0) otherwise yields NaN/Infinity y-coordinates, which
  // blanks the chart line.
  const getHashratePercentage = (value: number) => 1000 - (
    hashrateMaximized
      ? value / ((max || 1) / 1000)
      : (value - min) / (((max - min) || 1) / 1000)
  );

  const seriesY = {
    hashrate: getHashratePercentage,
    scoring_hashrate: getHashratePercentage,
    workers_active: (value: number) => 1000 - (value) / (maxWorkers || 1) * 1000,
  };

  // Project each bucket onto the time axis.
  const points: PlotPoint[] = hashRatesSlice.map((point) => {
    const t = new Date(point.date).getTime();

    return {
      t,
      x: ((t - startMs) / span) * GRAPH_WIDTH,
      hashrate: parseFloat(point.hashrate),
      scoring_hashrate: parseFloat(point.scoring_hashrate),
      workers_active: point.workers_active,
    };
  });

  const visibleSeries = SERIES_KEYS.filter((key) => graphVisibleElements.includes(key));

  // Only connect adjacent buckets; a gap larger than ~1.5 buckets (a miner
  // offline, missing snapshots) leaves a real hole instead of a smeared line.
  const maxGap = bucketMs * 1.5;

  const onMove = (event: React.MouseEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();

    if (!rect.width || !points.length) {
      return;
    }
    const targetX = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) * GRAPH_WIDTH;
    let nearest = 0;
    let best = Infinity;

    points.forEach((point, index) => {
      const distance = Math.abs(point.x - targetX);

      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });
    setHover(nearest);
  };

  const active = hover !== null && hover < points.length ? points[hover] : null;
  const tooltipLeft = active ? Math.min(88, Math.max(12, (active.x / GRAPH_WIDTH) * 100)) : 0;

  return (
    <div className='graph'>
      <GraphFrame steps={0} verticalGuides={ticks.map((tick) => tick.fraction)}>
        { isLoading ? (
          <GraphLoading />
        ) : (
          <>
            { points.map((point, index) => {
              const next = points[index + 1];

              if (!next || next.t - point.t > maxGap) {
                return null;
              }
              return visibleSeries.map((key, i) => {
                const y1 = seriesY[key](point[key]);
                const y2 = seriesY[key](next[key]);

                return (
                  <React.Fragment key={`${index}-key-${i}`}>
                    <line x1={point.x} x2={next.x} y1={y1} y2={y2} className={key} />
                    <polygon
                      points={`${point.x},${y1} ${next.x},${y2} ${next.x},1000 ${point.x},1000 ${point.x},${y1}`}
                      style={{ fill: `url(#${key})` }}
                      className='graph_poly'
                    />
                  </React.Fragment>
                );
              });
            }) }
            { active && (
              <line x1={active.x} x2={active.x} y1={0} y2={1000} className='crosshair' />
            ) }
            { active && visibleSeries.map((key) => (
              <circle key={key} cx={active.x} cy={seriesY[key](active[key])} r={8} className={`dot ${key}`} />
            )) }
            <rect
              className='hover-capture'
              x={0}
              y={0}
              width={GRAPH_WIDTH}
              height={1000}
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            />
          </>
        )}
      </GraphFrame>
      { active && (
        <div className='chart-tooltip' style={{ left: `${tooltipLeft}%` }}>
          <strong>{formatBucketTime(new Date(active.t), timezone)}</strong>
          <span>Hashrate: {fmtHashrate(active.hashrate)}</span>
          <span>Scoring Hashrate: {fmtHashrate(active.scoring_hashrate)}</span>
          <span>Active Workers: {active.workers_active}</span>
        </div>
      ) }
    </div>
  );
};

export default HashrateGraph;
